import { useRouter } from "next/navigation";
import React, { useCallback, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";

import LabelButtonWrapper from "@/components/atoms/button-wrapper/button-wrapper";
import withBrand from "@/components/with-brand";
import {
  AMT_POLICY_START_DATE_MAX_DAYS,
  ANNUAL_MULTI_TRIP,
  SINGLE_TRIP,
} from "@/constants";
import useUpdateTripDetails, {
  UpdateProps,
} from "@/hooks/api/quote/update-trip-details";
import { useFormatDate } from "@/libs/i18n/utils.client";
import { useLoading } from "@/providers/loading-provider";
import { ToggleModalContentType } from "@/schema/quote-page/page.schema";
import { RootState } from "@/store/root-reducer";
import { setApiError } from "@/store/slices/errors/api-error.slice";
import {
  clearQuote,
  setTripTypeChangedState,
  updateQuote,
} from "@/store/slices/quote/quote.slice";
import { QuoteData } from "@/types/quote/quote-data";
import { apiErrorMappings } from "@/utils/errors/api-error-map";
import { calculateDateDifference } from "@/utils/travel-date.utils";

type StAmtTogleModalProps = {
  brand?: "avanti" | "staysure";
  isOpenViaBottomToggle?: boolean;
  onCancel: () => void;
  onNewDate: () => void;
  onUpdated?: () => void;
};

function setPatchBody(
  attributes: QuoteData["attributes"],
  postData: UpdateProps["requestdata"]
) {
  let data = attributes.tripDetails;
  if (data) data = { ...data, ...postData };
  if (data?.policyType === SINGLE_TRIP) {
    delete data.area;
  }
  return data;
}

function StAmtTogleModalCancellation({
  onNewDate,
  onCancel,
  onUpdated,
  stAmtToggleCancellaionTitle,
  stAmtToggleCancellaionDescription,
  stAmtToggleCancellaionDescriptionMoreThanNinetyDays,
  stAmtToggleCancellaionContinueBtnLabele,
  stAmtToggleCancellaionNewDateBtnLabel,
  isOpenViaBottomToggle,
}: Readonly<StAmtTogleModalProps & Partial<ToggleModalContentType>>) {
  const dispatch = useDispatch();
  const router = useRouter();
  const { showLoading, hideLoading } = useLoading();
  const { attributes } = useSelector((state: RootState) => state.quote);
  const formatDate = useFormatDate();

  const [updateTriDetails, setUpdateTriDetails] = React.useState(false);

  /* istanbul ignore next */
  const resetTrigger = useCallback(() => {
    setUpdateTriDetails(false);
    hideLoading();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const { data, loading, error, updateTripDetails } = useUpdateTripDetails({
    resetTrigger,
  });

  const TabToggleHandler = () => {
    const requestdata = {
      policyType: ANNUAL_MULTI_TRIP,
      initialQuotePageLoading: true,
    };

    const bodyData = setPatchBody(attributes, requestdata);
    updateTripDetails(bodyData);
  };

  useEffect(() => {
    const handleEffect = async () => {
      if (!loading && data && !error) {
        dispatch(updateQuote(data));
        if (isOpenViaBottomToggle) {
          dispatch(setTripTypeChangedState(true));
        }
        onCancel();
      } else if (!loading && error !== null) {
        if (error.response?.status === 401) {
          dispatch(clearQuote());
          router.push("/");
        } else {
          onCancel();
          const errorObject = await apiErrorMappings(error);
          errorObject.multiButton = true;
          dispatch(setApiError(errorObject));
        }
      }
    };
    handleEffect();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, loading, error, dispatch, onCancel, router]);

  const handleContinue = () => {
    TabToggleHandler();
    if (onUpdated !== undefined) onUpdated();
    showLoading();
  };

  if (updateTriDetails) {
    return null;
  }

  const stStartDate = attributes.tripDetails?.startDate
    ? new Date(attributes.tripDetails?.startDate)
    : new Date();

  const dateDiff = calculateDateDifference(stStartDate);

  return (
    <div data-testid="st-amt-canceltion-modal-id">
      <div>
        <h1 className="text-text-title md:mb-spacing-s mb-spacing-xs md:text-[31px] text-[23px] font-header-font font-[400] leading-[28px] md:leading-[34px]">
          {stAmtToggleCancellaionTitle}
        </h1>
        <p className="font-body-font font-[400] leading-[150%] text-[18px] text-text-body">
          {dateDiff < AMT_POLICY_START_DATE_MAX_DAYS
            ? stAmtToggleCancellaionDescription?.replace(
                "[start_date]",
                formatDate(attributes.tripDetails?.startDate ?? "", "medium")
              )
            : stAmtToggleCancellaionDescriptionMoreThanNinetyDays}
        </p>
      </div>

      <div className="md:flex md:justify-between mt-spacing-l">
        {dateDiff < AMT_POLICY_START_DATE_MAX_DAYS && (
          <LabelButtonWrapper
            id="st-amt-toggle-modal-update-button"
            hierarchy="primary"
            label={stAmtToggleCancellaionContinueBtnLabele ?? "Update"}
            className="md:w-[148px] w-full mr-[0px] mb-[8px] md:mb-[0px] text-[18px] font-body-font h-[48px] max-h-[48px] font-[600] leading-[100%] text-text-on-primary"
            onClick={() => {
              handleContinue();
            }}
          />
        )}

        <LabelButtonWrapper
          id="st-amt-toggle-modal-cancel-button"
          hierarchy="secondary"
          label={stAmtToggleCancellaionNewDateBtnLabel ?? "Cancel"}
          className={`w-full text-[18px] font-body-font h-[48px] max-h-[48px] font-[600] leading-[100%] text-interactive-secondary-default ${
            dateDiff >= AMT_POLICY_START_DATE_MAX_DAYS
              ? "md:[width:-webkit-fill-available]"
              : "md:w-[148px]"
          }`}
          onClick={() => {
            if (onNewDate !== undefined) {
              onNewDate();
            }
          }}
        />
      </div>
    </div>
  );
}

export default withBrand(StAmtTogleModalCancellation);
